// Employee DAO — data access for employees.

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';
import type { Employee } from '@shared/types/employee.ts';
import type { PostgrestError } from '@supabase/supabase-js';

export class EmployeeDao extends BaseDao<Employee> {
  protected readonly dependentResources = ['employees'];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'employees', invalidate);
  }

  /** Get active employee by email. Returns null if not found. */
  async getByEmail(email: string): Promise<Employee | null> {
    const { data, error }: { data: Employee | null; error: PostgrestError | null } = await this.sb
      .from('employees')
      .select('*')
      .eq('email', email)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return data;
  }

  /** Get active employee by auth user ID. Returns null if not found. */
  async getByUserId(userId: string): Promise<Employee | null> {
    const { data, error }: { data: Employee | null; error: PostgrestError | null } = await this.sb
      .from('employees')
      .select('*')
      .eq('user_id', userId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return data;
  }

  /** Get roles for an active employee by email. Returns [] if not found. */
  async getRolesByEmail(email: string): Promise<string[]> {
    const { data, error } = await this.sb
      .from('employees')
      .select('roles')
      .eq('email', email)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return (data as { roles: string[] } | null)?.roles ?? [];
  }

  /** Get roles for an active employee by auth user ID. Returns [] if not found. */
  async getRolesByUserId(userId: string): Promise<string[]> {
    const { data, error } = await this.sb
      .from('employees')
      .select('roles')
      .eq('user_id', userId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return (data as { roles: string[] } | null)?.roles ?? [];
  }
}
